import type {SessionState} from '@github-ui/agent-sessions/types/session'

import type {RequireAtLeastOne} from '../../helpers/type-utilities'
import type {MemexColumn, SystemColumnId} from '../columns/contracts/memex-column'
import type {
  LocalUpdatePayload,
  MemexColumnData,
  MemexItemColumnUpdateData,
  RemoteUpdatePayload,
} from '../columns/contracts/storage'
import type {IAssignee, IssueType, Label, Milestone, StateReason, User} from '../common-contracts'
import type {ItemType} from './item-type'

export const REQUEST_ID_HEADER = 'X-Github-Request-Id'

export const NO_GROUP_VALUE = '_noValue'

type Optional<T extends object, P extends keyof T> = Omit<T, P> & {[K in P]?: T[K]}

type JobWithUrl = {
  job: {
    url: string
  }
}

export interface AddMemexItemRequest {
  memexProjectItem: MemexItemCreateData
}

export interface AddMemexItemResponse {
  memexProjectItem: MemexItem
  memexProjectColumn: null | Array<Pick<MemexColumn, 'id' | 'partialFailures'>>
}

export type BulkAddItem = {
  userName: string
  repoName: string
  number: number
  hasSubIssues: boolean
}

export interface BulkAddMemexItemsRequest {
  memexProjectItem: {
    contentType: typeof ItemType.DraftIssue
    content: {
      title: string
    }
    memexProjectColumnValues?: Array<RemoteUpdatePayload>
  }
}

export interface BulkAddRequest {
  count: number
  initialImport?: boolean
}

export interface BulkAddByRepository extends BulkAddRequest {
  repositoryId: number
  contentTypes: Array<string>
}

export interface BulkAddByQuery extends BulkAddRequest {
  q: string
}

export type RepositoryBulkAddMemexItemsRequest = BulkAddByRepository | BulkAddByQuery

export type BulkAddMemexItemsResponse = JobWithUrl & {
  notParsedContent: string
}

export type BulkUpdateMemexItemsRequest = {
  fieldIds: ReadonlyArray<SystemColumnId | number>
  memexProjectItems: Array<{id: number} & MemexItemColumnUpdateData>
}

export type BulkUpdateError = {
  message: string
  memexProjectItemId: number | string
}

export interface BulkUpdateMemexItemsResponse {
  job?: string
  totalUpdatedItems: number
  memexProjectItems?: Array<UpdateMemexItemResponseData>
  bulkUpdateErrors?: Array<BulkUpdateError>
  errors?: Array<string>
  invalidateQueryCache?: boolean
  requestId?: string
}

export interface GetMemexItemRequest {
  memexProjectItemId: number
}

export interface GetMemexItemResponse {
  memexProjectItem: MemexItem
}

export type UpdateMemexItemResponseData = Optional<MemexItem, 'memexProjectColumnValues' | 'content'>

export type IUpdateMemexItemRequest = MemexItemUpdateData & {
  memexProjectItemId: number
  fieldIds: ReadonlyArray<SystemColumnId | number>
}

export interface UpdateMemexItemResponse {
  memexProjectItem: UpdateMemexItemResponseData
  invalidateQueryCache?: boolean
}

export type RemoveMemexItemRequest =
  | {
      memexProjectItemIds: Array<number>
    }
  | {
      q: string
      scope: PaginatedItemsScope
    }

export interface ArchiveMemexItemRequest {
  memexProjectItemIds: Array<number>
}

export type UnarchiveMemexItemRequest =
  | {
      memexProjectItemIds: Array<number>
    }
  | {q: string}

export type IRemoveMemexItemResponse = void | JobWithUrl

export type ArchiveMemexItemResponse = void

export type UnarchiveMemexItemResponse = void | JobWithUrl

export interface ConvertDraftItemToIssueRequest {
  memexProjectItemId: number
  repositoryId: number
}

export interface ConvertDraftItemToIssueResponse {
  memexProjectItem: MemexItem
  warnings?: {
    invalidAssigneeLogins?: Array<string>
  }
}

interface GetSuggestedMetadataRequest {
  memexProjectItemId: number
}
export type IGetSuggestedAssigneesRequest = GetSuggestedMetadataRequest

export interface GetSuggestedAssigneesResponse {
  suggestions: Array<SuggestedAssignee>
}

export type IGetSuggestedLabelsRequest = GetSuggestedMetadataRequest

export interface GetSuggestedLabelsResponse {
  suggestions: Array<SuggestedLabel>
}

export type IGetSuggestedMilestonesRequest = GetSuggestedMetadataRequest

export interface GetSuggestedMilestonesResponse {
  suggestions: Array<SuggestedMilestone>
}

export type IGetSuggestedIssueTypesRequest = GetSuggestedMetadataRequest

export interface GetSuggestedIssueTypesResponse {
  suggestions: Array<SuggestedIssueType>
}

export interface SuggestedAssignee extends IAssignee {
  selected: boolean
}

export interface SuggestedLabel extends Label {
  selected: boolean
}

export interface SuggestedMilestone extends Milestone {
  selected: boolean
}

export interface SuggestedIssueType extends IssueType {
  selected: boolean
}

interface MemexItemDraftIssueCreateData {
  contentType: typeof ItemType.DraftIssue
  content: {
    title: string
  }
  memexProjectColumnValues?: Array<RemoteUpdatePayload>
  localColumnValues?: Array<LocalUpdatePayload>
  previousMemexProjectItemId?: number | ''
}

interface MemexItemIssueCreateData {
  contentType: typeof ItemType.Issue
  content: {
    id: number
    repositoryId: number
  }
  memexProjectColumnValues?: Array<RemoteUpdatePayload>
  localColumnValues?: Array<LocalUpdatePayload>
  previousMemexProjectItemId?: number | ''
}

interface MemexItemPullRequestCreateData {
  contentType: typeof ItemType.PullRequest
  content: {
    id: number
    repositoryId: number
  }
  memexProjectColumnValues?: Array<RemoteUpdatePayload>
  localColumnValues?: Array<LocalUpdatePayload>
  previousMemexProjectItemId?: number | ''
}

export type MemexItemCreateData =
  | MemexItemDraftIssueCreateData
  | MemexItemIssueCreateData
  | MemexItemPullRequestCreateData

interface ReprioritizeMemexItemData {
  previousMemexProjectItemId: number | ''
  parentIssueId?: number
}

/**
 * Requires that at least one of previousMemexProjectItemId or memexProjectColumnValues is defined
 * For the following declarations:
 * const data1: MemexItemUpdateData = {}
 * const data2: MemexItemUpdateData = {memexProjectColumnValues: []}
 * const data3: MemexItemUpdateData = {previousMemexProjectItemId: ''}
 * const data4: MemexItemUpdateData = {memexProjectColumnValues: [], previousMemexProjectItemId: ''}
 * data1 will generate a compiler error, while all of the others are type-safe
 */
export type MemexItemUpdateData = RequireAtLeastOne<ReprioritizeMemexItemData & MemexItemColumnUpdateData>

export interface ItemContent {
  id: number
}

export interface IssueOrPullRequestContent extends ItemContent {
  url: string
}

export type PullRequestContent = IssueOrPullRequestContent
export interface IssueContent extends IssueOrPullRequestContent {
  globalRelayId?: string
}

export type DraftIssueContent = ItemContent
export type RedactedItemContent = ItemContent

export interface ArchivedInfo {
  archivedAt: string
  archivedBy?: User
}

export interface AgentInfo {
  login: string
  avatarUrl: string
  displayName: string
}

export interface AgentTask {
  state: SessionState
  title?: string
  createdAt?: string
  lastUpdatedAt?: string
  agentId?: number
  id?: string
  repoNwo: string
  agent?: AgentInfo
  assignmentId?: number
  sourceRepoNwo?: string
}

export interface MemexItemBase {
  id: number
  priority: number | null
  virtualPriority: string | null
  updatedAt?: string
  createdAt?: string
  memexProjectColumnValues: Array<MemexColumnData>
  archived?: ArchivedInfo
  issueCreatedAt?: string
  issueClosedAt?: string
  state?: 'closed' | 'open'
  stateReason?: StateReason
  blockedByCount?: number
  agentTasks?: Array<AgentTask>
}

export interface RedactedItem extends MemexItemBase {
  contentType: typeof ItemType.RedactedItem
  content: RedactedItemContent
}

export interface DraftIssue extends MemexItemBase {
  contentType: typeof ItemType.DraftIssue
  content: DraftIssueContent
}

export interface PullRequest extends MemexItemBase {
  contentType: typeof ItemType.PullRequest
  content: PullRequestContent
  contentRepositoryId: number
}

export interface Issue extends MemexItemBase {
  contentType: typeof ItemType.Issue
  content: IssueContent
  contentRepositoryId: number
}

export interface AgentTaskItem extends MemexItemBase {
  contentType: typeof ItemType.AgentTask
  content: {id: number}
  contentRepositoryId: number
}

export type MemexItem = RedactedItem | DraftIssue | PullRequest | Issue | AgentTaskItem

export type GetArchivedMemexItemsRequest = {
  perPage?: number
  visibleFields?: Array<SystemColumnId | number>
}

export type GetArchivedMemexItemsResponse = {
  totalCount: number
  memexProjectItems: Array<MemexItem>
}

export type PaginatedItemsScope = 'all' | 'archived' | 'unarchived'

export type ResyncElasticsearchIndexResponse = JobWithUrl
